import { Flex, HStack, Td, Text, Tfoot, Tr } from "@chakra-ui/react";
import {
  calculateTax,
  defaultTaxValue,
  TaxKey,
  taxMapAtom,
  TaxSelect,
} from "../TaxSelect";
import { useAtom } from "jotai";
import {
  showCO2Atom,
  showPricesAtom,
  showTaxAtom,
} from "../../compositions/modals/ConfigureCompareViewModal";
import { useMemo } from "react";
import PriceDisplay from "../../elements/PriceDisplay";
import { ScenarioModel } from "../../clients/KompasApi";
import { FixedNumberDisplay } from "../../elements/NumberDisplay";
import { hasFeature } from "../../features";
import { DividerRow } from "./DividerRow";
import { useComparePageContext } from "../../shared/state/ComparePageContext";
import { TextPopover } from "../TextPopover";

interface PriceCellProps {
  price: number;
  scenarioId: number;
  taxKey: TaxKey;
  whatsIncludedPopupContent?: React.ReactNode;
}
function PriceCell({
  price,
  scenarioId,
  taxKey,
  whatsIncludedPopupContent = null,
}: PriceCellProps) {
  const [taxMap] = useAtom(taxMapAtom);
  const [showTax] = useAtom(showTaxAtom);

  const tax = useMemo(
    () => taxMap[scenarioId]?.[taxKey] ?? defaultTaxValue,
    [scenarioId, taxKey, taxMap],
  );

  const totalPrice = useMemo(
    () => price + calculateTax(price, tax),
    [price, tax],
  );

  return (
    <Td>
      <HStack justifyContent="space-between">
        <Flex
          alignItems="center"
          flex={0}
          gap={2}
          justifyContent="start"
          whiteSpace="nowrap"
        >
          <TextPopover inverted popoverSize="3xl" trigger="Totaal">
            {whatsIncludedPopupContent}
          </TextPopover>
          {showTax && (
            <Flex alignItems="inherit">
              (inclusief{" "}
              <TaxSelect scenarioId={scenarioId} taxKey={taxKey} value={tax} />)
            </Flex>
          )}
        </Flex>
        <PriceDisplay fontWeight="bold" price={showTax ? totalPrice : price} />
      </HStack>
    </Td>
  );
}

interface CO2CellProps {
  co2Impact: number;
  whatsIncludedPopupContent?: React.ReactNode;
}
function CO2Cell({
  co2Impact,
  whatsIncludedPopupContent = null,
}: CO2CellProps) {
  return (
    <Td>
      <HStack justifyContent="space-between">
        <TextPopover
          inverted
          popoverSize="3xl"
          trigger={
            <Text flex={1}>
              CO<sub>2</sub>-impact materialen
            </Text>
          }
        >
          {whatsIncludedPopupContent}
        </TextPopover>
        <Text fontWeight="bold">
          <FixedNumberDisplay decimals={0} number={co2Impact} suffix="kg" />
        </Text>
      </HStack>
    </Td>
  );
}

interface CategoryFooterProps {
  calculatePrice: (scenario: ScenarioModel) => number;
  calculateCO2Impact: (scenario: ScenarioModel) => number;
  taxKey: TaxKey;
  whatsIncludedPricePopupContent?: React.ReactNode;
  whatsIncludedCO2PopupContent?: React.ReactNode;
}
export function CategoryFooter({
  calculatePrice,
  calculateCO2Impact,
  taxKey,
  whatsIncludedPricePopupContent = null,
  whatsIncludedCO2PopupContent = null,
}: CategoryFooterProps) {
  const { scenarios } = useComparePageContext();
  const [showPrices] = useAtom(showPricesAtom);
  const [showCO2] = useAtom(showCO2Atom);

  if ((!showPrices && !showCO2) || !hasFeature("pricing")) {
    return null;
  }

  return (
    <Tfoot>
      <DividerRow />
      {showCO2 && (
        <Tr>
          <Td />
          <Td />
          {scenarios.map((scenario) => (
            <CO2Cell
              key={scenario.id}
              co2Impact={calculateCO2Impact(scenario)}
              whatsIncludedPopupContent={whatsIncludedCO2PopupContent}
            />
          ))}
        </Tr>
      )}
      {showPrices && (
        <Tr>
          <Td />
          <Td />
          {scenarios.map((scenario) => (
            <PriceCell
              key={scenario.id}
              price={calculatePrice(scenario)}
              scenarioId={scenario.id}
              taxKey={taxKey}
              whatsIncludedPopupContent={whatsIncludedPricePopupContent}
            />
          ))}
        </Tr>
      )}
    </Tfoot>
  );
}
